import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { fetchBrand, fetchBusinessSettings, validateDiscountCode, createStripeCheckout, fetchSavedCards, deleteSavedCard, fetchFavourites } from "@/lib/api";
import { stashPendingFavourites, readDeviceFavourites, resolveFavourites } from "@/components/FavouritesRail";
import { formatMoney } from "@/lib/orderUtils";
import { LoyaltyPanel, LoyaltyPromoBanner, SlotPicker, CrossSell } from "@/lib/checkoutExtras";
import { ModifierPicker } from "@/lib/ModifierPicker";
import { groupItemsByCategory } from "@/lib/menuGrouping";
import { useMemberDiscountRule, computeMemberDiscount, memberDiscountLabel } from "@/lib/memberDiscount";

import CategoryNav from "@/components/CategoryNav";
import { TagChips } from "@/components/TagChips";
import { BrandBadgeOverlay } from "@/components/BrandBadges";
import { computeBrandBadges } from "@/lib/brandBadges";
import { optimizedImage, optimizedSrcSet } from "@/lib/cloudinaryImage";
import { ImageLightbox } from "@/components/ImageLightbox";
import { brandOpenState } from "@/lib/brandAvailability";

const QRMenu = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [brand, setBrand] = useState(null);
    const [settings, setSettings] = useState(null);
    const [lines, setLines] = useState([]);
    const [showCheckout, setShowCheckout] = useState(false);
    const [customerName, setCustomerName] = useState("");
    const [notes, setNotes] = useState("");
    const [payMethod, setPayMethod] = useState("card");
    const [placing, setPlacing] = useState(false);
    const [member, setMember] = useState(null);
    const [redeemPoints, setRedeemPoints] = useState(0);
    const [scheduledFor, setScheduledFor] = useState(null);
    const [pickerItem, setPickerItem] = useState(null);
    const [lightboxImage, setLightboxImage] = useState(null); // { src, alt }

    // Delivery / collection state
    const [orderType, setOrderType] = useState("collection"); // 'collection' | 'delivery'
    const [deliveryAddr, setDeliveryAddr] = useState({
        address_line_1: "", address_line_2: "", city: "", postcode: "", instructions: "", phone: "",
    });
    // If deliveries are globally disabled, force collection.
    useEffect(() => {
        if (settings && settings.deliveries_enabled === false && orderType === "delivery") {
            setOrderType("collection");
        }
    }, [settings, orderType]);
    // Discount code
    const [discountInput, setDiscountInput] = useState("");
    const [appliedDiscount, setAppliedDiscount] = useState(null); // {code, amount, type, value}
    const [discountError, setDiscountError] = useState("");
    const [validatingCode, setValidatingCode] = useState(false);

    // Saved cards (loyalty members only)
    const [savedCards, setSavedCards] = useState([]);
    const [selectedCardId, setSelectedCardId] = useState(null);

    useEffect(() => {
        fetchBrand(slug).then(setBrand).catch(() => navigate("/brands"));
        fetchBusinessSettings().then(setSettings).catch(() => {});
    }, [slug, navigate]);

    // "Your favourites" for this brand: member history first, else this device.
    const [memberFavRows, setMemberFavRows] = useState([]);
    useEffect(() => {
        if (!member?.id) { setMemberFavRows([]); return; }
        fetchFavourites(member.id, 20).then((r) => setMemberFavRows(r?.items || [])).catch(() => setMemberFavRows([]));
    }, [member?.id]);
    const favItems = useMemo(() => {
        if (!brand) return [];
        const pick = (rows) => resolveFavourites(rows.filter((r) => r.brand_id === brand.id), [brand], 6).map((f) => f.item);
        const fromMember = pick(memberFavRows);
        return fromMember.length ? fromMember : pick(readDeviceFavourites());
    }, [brand, memberFavRows]);

    // Reload saved cards whenever the member signs in or changes
    useEffect(() => {
        if (member?.id) {
            fetchSavedCards(member.id)
                .then((r) => setSavedCards(r.cards || []))
                .catch(() => setSavedCards([]));
        } else {
            setSavedCards([]);
            setSelectedCardId(null);
        }
    }, [member?.id]);

    // Live-computed delivery fee (null = N/A, -1 = postcode not delivered to, >=0 = fee)
    const deliveryFee = useMemo(() => {
        if (orderType !== "delivery" || !settings) return null;
        const pc = (deliveryAddr.postcode || "").toUpperCase().replace(/\s+/g, "");
        if (!pc) return null;
        let best = null;
        for (const z of settings.delivery_zones || []) {
            const prefix = (z.postcode_prefix || "").toUpperCase().replace(/\s+/g, "");
            if (prefix && pc.startsWith(prefix)) {
                if (!best || prefix.length > best.prefix.length) best = { prefix, fee: z.fee };
            }
        }
        return best ? best.fee : -1;
    }, [orderType, deliveryAddr.postcode, settings]);

    const addItem = (item, mods = [], priceOverride = null) => {
        if (!brand) return;
        // Open picker if item has modifiers and none were chosen yet
        if ((item.modifier_groups?.length || 0) > 0 && mods.length === 0 && priceOverride === null) {
            setPickerItem(item);
            return;
        }
        const modDelta = mods.reduce((s, m) => s + (m.price_delta || 0), 0);
        const unitPrice = priceOverride !== null ? priceOverride : Math.round((item.price + modDelta) * 100) / 100;
        const sig = mods.map((m) => `${m.group_name}:${m.option_name}`).sort().join("|");
        setLines((cur) => {
            const idx = cur.findIndex((l) => l.item_id === item.id && (l._modSig || "") === sig);
            if (idx >= 0) {
                const n = [...cur];
                n[idx] = { ...n[idx], qty: n[idx].qty + 1 };
                return n;
            }
            return [
                ...cur,
                {
                    item_id: item.id, brand_id: brand.id, brand_name: brand.name,
                    brand_token: brand.token, brand_accent: brand.accent,
                    category: item.category || null,
                    name: item.name, price: unitPrice, base_price: item.price, qty: 1,
                    modifiers: mods, _modSig: sig,
                },
            ];
        });
    };

    const addCrossSell = (it) => {
        // Cross-sell payload has brand_id/brand_name/brand_token/brand_accent + item info
        // It's always a same-brand item via fetchRelatedItems (we treat as no-modifier quick add).
        setLines((cur) => {
            const idx = cur.findIndex((l) => l.item_id === it.item_id && !l._modSig);
            if (idx >= 0) {
                const n = [...cur];
                n[idx] = { ...n[idx], qty: n[idx].qty + 1 };
                return n;
            }
            return [
                ...cur,
                {
                    item_id: it.item_id, brand_id: it.brand_id, brand_name: it.brand_name,
                    brand_token: it.brand_token, brand_accent: it.brand_accent,
                    name: it.name, price: it.price, base_price: it.price, qty: 1,
                    modifiers: [], _modSig: "",
                },
            ];
        });
    };

    const changeQty = (itemId, delta, modSig = null) => {
        setLines((cur) =>
            cur.map((l) =>
                l.item_id === itemId && (l._modSig || "") === (modSig || "")
                    ? { ...l, qty: l.qty + delta }
                    : l
            ).filter((l) => l.qty > 0)
        );
    };

    const subtotal = useMemo(
        () => Math.round(lines.reduce((s, l) => s + l.qty * l.price, 0) * 100) / 100,
        [lines]
    );
    const count = lines.reduce((s, l) => s + l.qty, 0);
    const loyaltyDiscount = Math.min((redeemPoints || 0) * 0.05, subtotal);

    // Fee breakdown (mirrors backend math)
    // Small-order fee: delivery only. QRMenu is single-brand so at most 1 fee.
    const smallOrderFee = useMemo(() => {
        if (orderType !== "delivery") return 0;
        if (!settings || !settings.small_order_threshold) return 0;
        return subtotal > 0 && subtotal < settings.small_order_threshold
            ? Number(settings.small_order_fee || 0)
            : 0;
    }, [subtotal, settings, orderType]);

    // Service fee: pct% of subtotal clamped to [min, max], collection + delivery.
    const serviceFee = useMemo(() => {
        if (!settings || subtotal <= 0) return 0;
        const pct = Number(settings.service_fee_pct || 10);
        const floor = Number(settings.service_fee_min || 0.99);
        const ceil = Number(settings.service_fee_max || 2.49);
        const raw = Math.round((subtotal * pct / 100) * 100) / 100;
        return Math.min(ceil, Math.max(floor, raw));
    }, [subtotal, settings]);

    const bagCharge = settings ? Number(settings.bag_charge || 0) : 0;

    const effectiveDeliveryFee = orderType === "delivery" && deliveryFee != null && deliveryFee >= 0 ? deliveryFee : 0;
    // Recompute from the code's rule so the amount tracks basket changes (server does the same).
    const codeDiscount = !appliedDiscount ? 0 : appliedDiscount.type === "percent"
        ? Math.round(subtotal * Number(appliedDiscount.value)) / 100
        : Math.min(Number(appliedDiscount.value || 0), subtotal);

    // Auto-applied per-brand promo badges (frontend mirror — backend is source of truth)
    const badgeResult = useMemo(
        () => computeBrandBadges({ lines, brands: brand ? [brand] : [], orderType }),
        [lines, brand, orderType]
    );
    const badgeDiscount = badgeResult.discount;
    const deliveryAfterBadge = badgeResult.freeDelivery ? 0 : effectiveDeliveryFee;

    const memberRule = useMemberDiscountRule();
    const memberDiscount = computeMemberDiscount(memberRule, { member, hasCode: !!appliedDiscount, orderType, subtotal });
    const totalDiscount = Math.min(loyaltyDiscount + codeDiscount + memberDiscount + badgeDiscount, subtotal);
    const finalTotal = Math.max(
        0,
        Math.round((subtotal - totalDiscount + deliveryAfterBadge + smallOrderFee + serviceFee + bagCharge) * 100) / 100
    );

    const applyDiscount = async () => {
        const code = (discountInput || "").trim().toUpperCase();
        if (!code) return;
        setValidatingCode(true);
        setDiscountError("");
        try {
            const r = await validateDiscountCode(code, subtotal, orderType);
            if (r.valid) {
                setAppliedDiscount(r);
                setDiscountError("");
            } else {
                setAppliedDiscount(null);
                setDiscountError(r.reason || "Invalid code");
            }
        } catch (e) {
            setDiscountError("Could not check code");
        } finally {
            setValidatingCode(false);
        }
    };

    const clearDiscount = () => {
        setAppliedDiscount(null);
        setDiscountInput("");
        setDiscountError("");
    };

    // Eligibility for "Place Order"
    const deliveryReady = orderType === "collection" || (
        deliveryFee != null && deliveryFee >= 0 &&
        deliveryAddr.address_line_1.trim() && deliveryAddr.postcode.trim() && deliveryAddr.phone.trim()
    );

    const placeOrder = async () => {
        if (orderType === "delivery" && !deliveryReady) {
            alert("Please complete the delivery address before placing the order.");
            return;
        }
        setPlacing(true);
        const draft = {
            source: "qr", lines,
            customer_name: customerName || null,
            notes: notes || null,
            pay_method: "card",  // QR is always card-via-Stripe — no cash path
            loyalty_member_id: member?.id || null,
            redeem_points: redeemPoints || 0,
            scheduled_for: scheduledFor || null,
            order_type: orderType,
            delivery: orderType === "delivery" ? deliveryAddr : null,
            discount_code: appliedDiscount?.code || null,
        };
        try {
            // ALWAYS route through Stripe — the kitchen never sees an unpaid ticket.
            const session = await createStripeCheckout(draft, selectedCardId || null);
            if (session?.url) {
                stashPendingFavourites(lines);
                window.location.href = session.url;
                return; // browser navigates away
            }
            throw new Error("Could not start payment");
        } catch (e) {
            console.error(e);
            alert(e.response?.data?.detail || "Failed to place order. Try again.");
        } finally { setPlacing(false); }
    };

    if (!brand) return <div className="min-h-screen bg-[var(--bg)] text-muted grid place-items-center">Loading…</div>;

    // Availability: full-page splash ONLY for coming-soon (brand not launched).
    // Closed-for-now brands still show the menu — customer can browse and place
    // an order for later (scheduled pickup/delivery).
    const availability = brandOpenState(brand);
    const closedNow = availability.reason === "closed_now";
    if (!availability.available && (availability.reason === "coming_soon" || closedNow)) {
        return (
            <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex flex-col" data-testid="qr-menu-unavailable" style={{ "--brand-color": brand.accent }}>
                <div className="relative">
                    {brand.image_url && (
                        <div className="h-64 sm:h-80 overflow-hidden">
                            <img src={optimizedImage(brand.image_url, { w: 800 })} alt={brand.name} className="w-full h-full object-cover opacity-40" />
                        </div>
                    )}
                    <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 0%, rgba(10,10,10,0.6) 60%, var(--bg) 100%)" }} />
                    <div className="absolute top-0 left-0 right-0 p-5">
                        <Link to="/brands" className="font-mono text-[10px] uppercase tracking-[0.22em] hover:text-gold" data-testid="qr-unavailable-back">← Brands</Link>
                    </div>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center text-center px-6 -mt-16">
                    <div className="logo-box mb-6" style={{ width: 96, height: 96, fontSize: 34 }}>{brand.token}</div>
                    <h1 className="font-display text-[48px] sm:text-[64px] leading-none mb-3" style={{ color: brand.accent }}>{brand.name}</h1>
                    <p className="text-muted mb-8 max-w-md">{brand.tagline}</p>
                    <div className="border border-[var(--gold)] px-8 py-6 bg-[var(--gold)]/5">
                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold mb-2">{closedNow ? "// Not taking orders" : "// Coming soon"}</p>
                        <p className="font-display text-[28px] text-gold" data-testid="qr-unavailable-message">
                            {closedNow ? availability.nextOpenLabel : "Opening soon"}
                        </p>
                    </div>
                    <Link to="/brands" className="btn-secondary mt-8 text-sm px-6 py-3">← Browse other brands</Link>
                </div>
            </div>
        );
    }

    // Closed-for-now banner (menu still visible below)
    const showClosedBanner = !availability.available;

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] pb-32" data-testid="qr-menu-page" style={{ "--brand-color": brand.accent }}>
            {/* Closed-for-now banner — sticky at the very top. Menu remains
                browsable and orders remain placeable (they'll be scheduled for
                a future collection/delivery window at checkout). */}
            {showClosedBanner && (
                <div
                    className="sticky top-0 z-30 bg-[var(--bg)] border-b border-[var(--gold)] pt-safe px-4 py-3"
                    data-testid="qr-menu-closed-banner"
                >
                    <div className="max-w-3xl mx-auto flex items-center gap-3 justify-center flex-wrap">
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
                            // Currently closed
                        </span>
                        <span className="text-[13px] text-[var(--fg)]">
                            {availability.nextOpenLabel} — you can still browse and place an order for later.
                        </span>
                    </div>
                </div>
            )}
            {/* Brand hero */}
            <div className="relative">
                {brand.image_url && (
                    <div className="h-64 sm:h-80 overflow-hidden">
                        <img src={optimizedImage(brand.image_url, { w: 800 })} srcSet={optimizedSrcSet(brand.image_url, 800)} alt={brand.name} className="w-full h-full object-cover opacity-60" />
                    </div>
                )}
                <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent 0%, rgba(10,10,10,0.4) 60%, var(--bg) 100%)` }} />
                <BrandBadgeOverlay badges={brand.badges} etaMin={brand.eta_min} layout="hero" orderType={orderType} />
                <div className="absolute top-0 left-0 right-0 pt-safe-4 px-5 pb-5 flex items-center justify-between z-30">
                    <Link
                        to="/order"
                        className="font-mono text-[12px] uppercase tracking-[0.22em] text-white bg-black/70 backdrop-blur-md px-4 py-3 rounded-full hover:text-gold active:scale-95 transition shadow-lg"
                        data-testid="qr-back-to-brands"
                    >
                        ← All brands
                    </Link>
                    {brand.halal && <span className="halal-badge" style={{ position: "static" }}>Halal</span>}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-end gap-4">
                        <div className="logo-box" style={{ width: 56, height: 56, fontSize: 20 }}>{brand.token}</div>
                        <div>
                            <h1
                                className="font-display text-[34px] sm:text-[44px] leading-[0.95]"
                                style={{ color: brand.accent }}
                            >
                                {brand.name}
                            </h1>
                            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mt-1">
                                {brand.tagline}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: brand.accent }} />
            </div>

            {brand.daily_special && (
                <div className="bg-[var(--gold)] text-[var(--bg)] py-2.5 px-4 text-center font-mono uppercase tracking-[0.18em] text-[11px] font-bold" data-testid="qr-daily-special-banner">
                    ✦ Today: {brand.daily_special}
                </div>
            )}

            <div className="max-w-2xl mx-auto px-6 py-10">
                <p className="eyebrow">// Menu</p>
                <h2 className="font-display text-[32px] leading-[0.95] mt-3 mb-6">{brand.cuisine}</h2>
                {(() => {
                    const grouped = groupItemsByCategory(brand.items, brand.category_order || [], brand.popular_item_ids || [], brand.category_descriptions || {});
                    if (favItems.length) grouped.unshift({ category: "Your favourites", items: favItems, isPopular: true, isFavourite: true });
                    const slug = (s) => `qr-cat-${(s || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
                    const navCats = grouped.map(({ category }) => ({ id: slug(category), label: category }));
                    return (
                        <>
                            <CategoryNav categories={navCats} stickyOffsetPx={0} scrollOffsetPx={70} />
                            {grouped.map(({ category, items, isPopular, isFavourite, description }) => (
                    <div key={category} id={slug(category)} className="mb-10 scroll-mt-24" data-testid={`qr-cat-${category}`}>
                        <div className="mb-4 pb-3 border-b border-[var(--border)]">
                            <div className="flex items-baseline justify-between gap-3">
                                <h3 className="font-display font-bold text-[28px] sm:text-[32px] leading-tight flex items-baseline gap-2" style={{ color: isPopular ? "var(--gold)" : brand.accent }}>
                                    {isPopular && <span className="text-[18px]">{isFavourite ? "♥" : "★"}</span>}
                                    {category}
                                </h3>
                                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted shrink-0">
                                    {isFavourite ? "yours" : isPopular ? "7d" : items.length}
                                </span>
                            </div>
                            {description && (
                                <p className="text-[14px] sm:text-[15px] text-[var(--text)] mt-2 font-light leading-relaxed">{description}</p>
                            )}
                        </div>
                        <div className="divide-y divide-[var(--border)]">
                            {items.map((item) => {
                                const qty = lines.filter((l) => l.item_id === item.id).reduce((s, l) => s + l.qty, 0);
                                const firstLine = lines.find((l) => l.item_id === item.id);
                                return (
                                    <MenuItem
                                        key={item.id}
                                        item={item}
                                        qty={qty}
                                        onAdd={() => addItem(item)}
                                        onInc={() => changeQty(item.id, 1, firstLine?._modSig)}
                                        onDec={() => changeQty(item.id, -1, firstLine?._modSig)}
                                        onImageClick={() => setLightboxImage({ src: item.image_url, alt: item.name })}
                                    />
                                );
                            })}
                        </div>
                    </div>
                ))}
                        </>
                    );
                })()}
            </div>

            {count > 0 && !showCheckout && (
                <button
                    onClick={() => setShowCheckout(true)}
                    data-testid="qr-cart-bar"
                    className="fixed bottom-0 left-0 right-0 bg-[var(--gold)] text-[var(--bg)] pt-4 px-6 pb-safe-4 flex items-center justify-between font-mono uppercase tracking-[0.18em] text-sm z-30 font-bold"
                >
                    <span>{count} {count === 1 ? "item" : "items"} · View basket</span>
                    <span>{formatMoney(subtotal)} →</span>
                </button>
            )}

            {showCheckout && (
                <div className="fixed inset-0 bg-[#050505] z-40 overflow-y-auto" data-testid="qr-checkout-sheet">
                    <div className="min-h-screen flex flex-col">
                        <div className="border-b border-[var(--border)] px-5 pb-5 pt-safe-4 flex items-center justify-between">
                            <button onClick={() => setShowCheckout(false)} className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted" data-testid="qr-checkout-close">
                                ← Back to menu
                            </button>
                            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">// Basket</span>
                        </div>
                        <div className="flex-1 max-w-2xl mx-auto w-full px-6 py-8">
                            <p className="eyebrow">// Review &amp; Pay</p>
                            <h2 className="font-display text-[36px] leading-[0.95] mt-3 mb-8">
                                Your <span className="text-gold">order.</span>
                            </h2>
                            <div className="divide-y divide-[var(--border)] mb-8">
                                {lines.map((l) => (
                                    <div key={`${l.item_id}-${l._modSig || ""}`} className="py-4 flex items-center justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div>{l.name}</div>
                                            {l.modifiers?.length > 0 && (
                                                <div className="font-mono text-[11px] text-gold/80 leading-tight mt-0.5">
                                                    {l.modifiers.map((m) => m.option_name).join(" · ")}
                                                </div>
                                            )}
                                            <div className="font-mono text-[12px] text-muted">{formatMoney(l.price)}</div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <button onClick={() => changeQty(l.item_id, -1, l._modSig)} className="w-9 h-9 border border-[var(--border-strong)] grid place-items-center" data-testid={`qty-dec-${l.item_id}`}>−</button>
                                            <span className="font-mono w-6 text-center">{l.qty}</span>
                                            <button onClick={() => changeQty(l.item_id, 1, l._modSig)} className="w-9 h-9 border border-[var(--border-strong)] grid place-items-center" data-testid={`qty-inc-${l.item_id}`}>+</button>
                                        </div>
                                        <div className="font-mono w-20 text-right">{formatMoney(l.price * l.qty)}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Delivery / Collection toggle — hidden when deliveries_enabled=false */}
                            {(settings?.deliveries_enabled ?? true) ? (
                            <div className="grid grid-cols-2 mb-6 border-2 border-[var(--gold)]" data-testid="order-type-toggle">
                                <button
                                    onClick={() => setOrderType("delivery")}
                                    className={`py-4 font-display text-[20px] tracking-wide transition-colors ${
                                        orderType === "delivery"
                                            ? "bg-[#050505] text-[var(--gold)]"
                                            : "bg-transparent text-[var(--text)]/60 hover:text-[var(--text)]"
                                    }`}
                                    data-testid="order-type-delivery"
                                >
                                    {orderType === "delivery" && "✓ "}DELIVERY
                                </button>
                                <button
                                    onClick={() => setOrderType("collection")}
                                    className={`py-4 font-display text-[20px] tracking-wide transition-colors ${
                                        orderType === "collection"
                                            ? "bg-[#050505] text-[var(--gold)]"
                                            : "bg-transparent text-[var(--text)]/60 hover:text-[var(--text)]"
                                    }`}
                                    data-testid="order-type-collection"
                                >
                                    {orderType === "collection" && "✓ "}COLLECTION
                                </button>
                            </div>
                            ) : (
                                <div className="border-2 border-[var(--gold)] py-3 px-4 mb-6 text-center font-display text-[18px] tracking-wide text-gold" data-testid="qr-collection-only">
                                    ✓ COLLECTION AT COUNTER
                                </div>
                            )}

                            {/* Delivery address form */}
                            {orderType === "delivery" && (
                                <div className="mb-6 border border-[var(--border)] p-4 space-y-3" data-testid="delivery-form">
                                    <p className="eyebrow">// Delivery details</p>
                                    <input
                                        className="input"
                                        placeholder="Postcode (e.g. EH5 2AB)"
                                        value={deliveryAddr.postcode}
                                        onChange={(e) => setDeliveryAddr({ ...deliveryAddr, postcode: e.target.value.toUpperCase() })}
                                        data-testid="delivery-postcode"
                                    />
                                    {deliveryFee != null && deliveryFee >= 0 && (
                                        <div className="font-mono text-[12px] text-gold flex justify-between" data-testid="delivery-fee-line">
                                            <span>✓ Delivery to your postcode</span>
                                            <span>{formatMoney(deliveryFee)}</span>
                                        </div>
                                    )}
                                    {deliveryFee === -1 && (
                                        <div className="font-mono text-[12px] text-[#ff5246]" data-testid="delivery-not-available">
                                            Sorry, we don&apos;t deliver to that postcode yet.
                                        </div>
                                    )}
                                    <input
                                        className="input"
                                        placeholder="Address line 1"
                                        value={deliveryAddr.address_line_1}
                                        onChange={(e) => setDeliveryAddr({ ...deliveryAddr, address_line_1: e.target.value })}
                                        data-testid="delivery-line1"
                                    />
                                    <input
                                        className="input"
                                        placeholder="Address line 2 (optional)"
                                        value={deliveryAddr.address_line_2}
                                        onChange={(e) => setDeliveryAddr({ ...deliveryAddr, address_line_2: e.target.value })}
                                    />
                                    <input
                                        className="input"
                                        placeholder="City"
                                        value={deliveryAddr.city}
                                        onChange={(e) => setDeliveryAddr({ ...deliveryAddr, city: e.target.value })}
                                    />
                                    <input
                                        className="input"
                                        placeholder="Mobile (so the driver can call you)"
                                        value={deliveryAddr.phone}
                                        onChange={(e) => setDeliveryAddr({ ...deliveryAddr, phone: e.target.value })}
                                        data-testid="delivery-phone"
                                    />
                                    <textarea
                                        className="input"
                                        rows="2"
                                        placeholder="Delivery instructions (buzzer, flat number, gate code…)"
                                        value={deliveryAddr.instructions}
                                        onChange={(e) => setDeliveryAddr({ ...deliveryAddr, instructions: e.target.value })}
                                    />
                                </div>
                            )}

                            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">Your name (optional)</label>
                            <input className="input mb-5" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="e.g. Sam" data-testid="qr-name-input" />

                            <CrossSell lines={lines} onAdd={addCrossSell} />

                            <LoyaltyPromoBanner
                                member={member}
                                onJoinClick={() => document.querySelector('[data-testid="loyalty-join-open"]')?.click()}
                            />

                            <LoyaltyPanel
                                otherDiscount={codeDiscount + memberDiscount + badgeDiscount}
                                member={member} setMember={setMember}
                                redeemPoints={redeemPoints} setRedeemPoints={setRedeemPoints}
                                subtotal={subtotal}
                            />

                            {orderType === "collection" && (
                                <SlotPicker scheduledFor={scheduledFor} setScheduledFor={setScheduledFor} />
                            )}

                            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">Allergies / Special instructions</label>
                            <textarea className="input mb-6" rows="3" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Let the kitchen know…" data-testid="qr-notes-input" />

                            {/* Payment — card only for now (Apple Pay arrives with Stripe) */}
                            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-3">Payment</label>
                            {savedCards.length > 0 && payMethod === "card" && (
                                <div className="mb-4 space-y-2" data-testid="saved-cards-list">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold">// Your saved cards</div>
                                    {savedCards.map((c) => (
                                        <div key={c.id} className={`flex items-center gap-3 border p-3 cursor-pointer transition-colors ${selectedCardId === c.id ? "border-[var(--gold)] bg-[var(--gold)]/10" : "border-[var(--border)] hover:border-[var(--gold)]"}`} onClick={() => setSelectedCardId(selectedCardId === c.id ? null : c.id)} data-testid={`saved-card-${c.id}`}>
                                            <span className="font-mono text-[11px] uppercase tracking-[0.18em]" style={{ minWidth: 56 }}>
                                                {c.brand}
                                            </span>
                                            <span className="font-mono text-sm">•••• {c.last4}</span>
                                            <span className="font-mono text-[10px] text-muted">exp {String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}</span>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); if (window.confirm(`Remove ${c.brand.toUpperCase()} •••• ${c.last4}?`)) { deleteSavedCard(c.id, member.id).then(() => fetchSavedCards(member.id).then((r) => setSavedCards(r.cards || []))); }}}
                                                className="ml-auto font-mono text-[10px] uppercase tracking-[0.18em] text-muted hover:text-[#ff5246]"
                                                data-testid={`remove-card-${c.id}`}
                                            >
                                                ✕
                                            </button>
                                            {selectedCardId === c.id && (
                                                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold">✓ Selected</span>
                                            )}
                                        </div>
                                    ))}
                                    <div className="font-mono text-[10px] text-muted">
                                        {selectedCardId ? "Tap again to deselect — you'll be asked to choose any card on the next page." : "Or tap a card above for one-tap checkout."}
                                    </div>
                                </div>
                            )}
                            <div className="mb-6 p-4 border border-[var(--gold)] bg-[var(--surface-elevated)]" data-testid="qr-pay-card">
                                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold mb-1">Card · Apple Pay · Google Pay</div>
                                <div className="text-sm text-muted">Secure payment via Stripe — no cash accepted.</div>
                            </div>

                            {/* Order summary + Savings & Offers */}
                            <div className="border-t border-[var(--border)] pt-4 mb-6">
                                <p className="eyebrow mb-3">// Savings &amp; Offers</p>
                                {appliedDiscount ? (
                                    <div className="flex items-center justify-between mb-4 bg-[var(--surface-elevated)] border border-[var(--gold)] px-3 py-2" data-testid="applied-discount">
                                        <div>
                                            <div className="font-mono text-[12px] text-gold">✓ {appliedDiscount.code} applied</div>
                                            <div className="font-mono text-[10px] text-muted">
                                                {appliedDiscount.type === "percent" ? `${appliedDiscount.value}% off` : `£${appliedDiscount.value.toFixed(2)} off`}
                                            </div>
                                        </div>
                                        <button onClick={clearDiscount} className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted hover:text-[#ff5246]" data-testid="clear-discount">Remove</button>
                                    </div>
                                ) : (
                                    <div className="flex gap-2 mb-4">
                                        <input
                                            className="input flex-1 uppercase"
                                            placeholder="Enter code"
                                            value={discountInput}
                                            onChange={(e) => setDiscountInput(e.target.value)}
                                            data-testid="discount-input"
                                        />
                                        <button
                                            onClick={applyDiscount}
                                            disabled={validatingCode || !discountInput.trim()}
                                            className="btn-secondary disabled:opacity-40"
                                            data-testid="apply-discount-btn"
                                        >
                                            {validatingCode ? "…" : "Apply"}
                                        </button>
                                    </div>
                                )}
                                {discountError && (
                                    <div className="font-mono text-[11px] text-[#ff5246] mb-3" data-testid="discount-error">{discountError}</div>
                                )}

                                <div className="space-y-2">
                                    <div className="flex justify-between font-mono text-[12px] text-muted">
                                        <span>Subtotal</span><span>{formatMoney(subtotal)}</span>
                                    </div>
                                    {orderType === "delivery" && effectiveDeliveryFee > 0 && (
                                        <div className="flex justify-between font-mono text-[12px] text-muted" data-testid="summary-delivery">
                                            <span>Delivery</span><span>{formatMoney(effectiveDeliveryFee)}</span>
                                        </div>
                                    )}
                                    {smallOrderFee > 0 && (
                                        <div className="flex justify-between font-mono text-[12px] text-muted" data-testid="summary-small">
                                            <span title={`Orders under ${formatMoney(settings.small_order_threshold)} include a small order fee`}>
                                                Small order fee
                                            </span>
                                            <span>{formatMoney(smallOrderFee)}</span>
                                        </div>
                                    )}
                                    {serviceFee > 0 && (
                                        <div className="flex justify-between font-mono text-[12px] text-muted" data-testid="summary-service">
                                            <span>Service fee</span><span>{formatMoney(serviceFee)}</span>
                                        </div>
                                    )}
                                    {bagCharge > 0 && (
                                        <div className="flex justify-between font-mono text-[12px] text-muted" data-testid="summary-bag">
                                            <span>Bag</span><span>{formatMoney(bagCharge)}</span>
                                        </div>
                                    )}
                                    {loyaltyDiscount > 0 && (
                                        <div className="flex justify-between font-mono text-[12px] text-gold">
                                            <span>Loyalty −{redeemPoints} pts</span><span>−{formatMoney(loyaltyDiscount)}</span>
                                        </div>
                                    )}
                                    {memberDiscount > 0 && (
                                        <div className="flex justify-between font-mono text-[12px] text-gold" data-testid="summary-member-discount">
                                            <span>{memberDiscountLabel(memberRule)}</span><span>−{formatMoney(memberDiscount)}</span>
                                        </div>
                                    )}
                                    {codeDiscount > 0 && (
                                        <div className="flex justify-between font-mono text-[12px] text-gold" data-testid="summary-code-discount">
                                            <span>Code {appliedDiscount.code}</span><span>−{formatMoney(codeDiscount)}</span>
                                        </div>
                                    )}
                                    {badgeResult?.applied?.filter((b) => b.amount > 0).map((b, i) => (
                                        <div key={i} className="flex justify-between font-mono text-[12px] text-gold" data-testid={`summary-badge-${i}`}>
                                            <span>✓ {b.display}</span>
                                            <span>−{formatMoney(b.amount)}</span>
                                        </div>
                                    ))}
                                    {badgeResult?.freeDelivery && orderType === "delivery" && (
                                        <div className="flex justify-between font-mono text-[12px] text-gold" data-testid="summary-free-delivery">
                                            <span>✓ Free delivery applied</span><span>—</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-baseline pt-2 border-t border-[var(--border)]">
                                        <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted">Total</span>
                                        <span className="font-mono text-2xl text-gold" data-testid="qr-total">{formatMoney(finalTotal)}</span>
                                    </div>
                                </div>
                            </div>

                            <button onClick={placeOrder} disabled={placing || count === 0 || (orderType === "delivery" && !deliveryReady)} className="btn-primary w-full justify-center text-base py-4 disabled:opacity-40" data-testid="qr-place-order">
                                {placing ? "Placing…" :
                                    orderType === "delivery" && !deliveryReady ? "Complete delivery details →" :
                                    scheduledFor ? "Schedule order →" :
                                    orderType === "delivery" ? `Place delivery order · ${formatMoney(finalTotal)} →` :
                                    `Place collection order · ${formatMoney(finalTotal)} →`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {pickerItem && (
                <ModifierPicker
                    item={pickerItem}
                    onCancel={() => setPickerItem(null)}
                    onConfirm={(mods, totalPrice) => {
                        addItem(pickerItem, mods, totalPrice);
                        setPickerItem(null);
                    }}
                />
            )}

            {lightboxImage && (
                <ImageLightbox
                    src={lightboxImage.src}
                    alt={lightboxImage.alt}
                    onClose={() => setLightboxImage(null)}
                />
            )}
        </div>
    );
};

const MenuItem = ({ item, qty, onAdd, onInc, onDec, onImageClick }) => {
    const hasMods = (item.modifier_groups?.length || 0) > 0;
    return (
    <div className="py-4 border-b border-[var(--border)] last:border-b-0" data-testid={`menu-item-${item.id}`}>
        <div className="flex gap-4">
            <div className="flex-1 min-w-0 flex flex-col">
                <TagChips tags={[
                    ...(item.popular && !(item.tags || []).includes("best_seller") ? ["best_seller"] : []),
                    ...(item.tags || []),
                ]} />
                <h3 className="font-display text-[17px] sm:text-[18px] leading-snug mt-1.5 mb-1.5">
                    {item.name}
                </h3>
                {item.description && (
                    <p className="text-[13px] sm:text-[13.5px] text-[var(--text)]/65 font-light leading-relaxed mb-2">
                        {item.description}
                    </p>
                )}
                <div className="flex items-center gap-3 font-mono text-[12px] mt-auto pt-1">
                    <span className="text-gold text-[14px]">{formatMoney(item.price)}</span>
                    {item.calories && <span className="text-muted">{item.calories} kcal</span>}
                    {item.allergens?.length > 0 && (
                        <span className="text-muted" title={item.allergens.join(", ")}>⚠ {item.allergens.length}</span>
                    )}
                </div>
            </div>
            {item.image_url && (
                <button
                    type="button"
                    onClick={onImageClick}
                    className="relative shrink-0 w-24 h-24 sm:w-28 sm:h-28 overflow-hidden bg-[var(--surface-elevated)] group cursor-zoom-in p-0 border-0"
                    aria-label={`Enlarge ${item.name}`}
                    data-testid={`menu-img-${item.id}`}
                >
                    <img src={optimizedImage(item.image_url, { w: 120 })} srcSet={optimizedSrcSet(item.image_url, 120)} alt={item.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    <span className="absolute bottom-1 right-1 w-6 h-6 bg-black/55 text-white font-mono text-[11px] grid place-items-center" aria-hidden>⤢</span>
                </button>
            )}
        </div>
        <div className="flex justify-end mt-3">
            {qty > 0 && !hasMods ? (
                <div className="flex items-center gap-2">
                    <button onClick={onDec} className="w-9 h-9 border border-[var(--border-strong)] grid place-items-center" data-testid={`menu-dec-${item.id}`}>−</button>
                    <span className="font-mono w-6 text-center">{qty}</span>
                    <button onClick={onInc} className="w-9 h-9 border border-[var(--border-strong)] grid place-items-center" data-testid={`menu-inc-${item.id}`}>+</button>
                </div>
            ) : (
                <button onClick={onAdd} className="btn-ghost whitespace-nowrap" data-testid={`menu-add-${item.id}`}>
                    {hasMods ? (qty > 0 ? `+ Add (${qty})` : "+ Customise") : "+ Add"}
                </button>
            )}
        </div>
    </div>
    );
};

export default QRMenu;
